import { BusinessModelEnumOptions } from "@boundless/bl-api-client/customer/models/BusinessModelEnumLabels"
import { EstimatedRevenueEnumOptions } from "@boundless/bl-api-client/customer/models/EstimatedRevenueEnumLabels"
import { IndustryEnumOptions } from "@boundless/bl-api-client/customer/models/IndustryEnumLabels"
import { useCustomerPrequalifyingBusinessDetailsUpdate } from "@boundless/bl-api-client/customer/services"
import { CustomerPrequalifyingBusinessDetailsUpdateBody } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { FormChipGroup } from "@/components/form-elements/FormChipGroup"
import { FormSelect } from "@/components/form-elements/FormSelect"
import { OnboardingFormStep } from "@/components/onboarding/OnboardingFormStep"
import { type ErrorType } from "@/lib/clients"
import { onboardingBusinessErrorMap } from "@/lib/errors"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type { z } from "zod"

/** The row this step writes, exactly as the endpoint defines it. */
const schema = CustomerPrequalifyingBusinessDetailsUpdateBody

export type BusinessAnswers = z.infer<typeof schema>

/**
 * Every field is an enum with no empty member, so the unanswered state is cast
 * in: it is what the selects show a placeholder for, and the resolver rejects
 * it before it can be saved.
 */
const EMPTY = {
  industry: "" as BusinessAnswers["industry"],
  businessModel: "" as BusinessAnswers["businessModel"],
  estimatedRevenue: "" as BusinessAnswers["estimatedRevenue"],
}

interface Props {
  companyUuid: string
  /**
   * What is already in this step: the answer saved on an earlier pass, or the
   * draft left behind by walking away from it mid-answer. Partial, because a
   * draft is whatever had been typed at the moment Back was pressed.
   */
  answers: Partial<BusinessAnswers> | null
  onNext: (answers: BusinessAnswers) => void
  /** Handed whatever is in the fields, valid or not, so it survives the trip. */
  onBack: (draft: Partial<BusinessAnswers>) => void
}

/**
 * What public records don't reliably carry: industry, how they sell, and how
 * much they take. All three change which lenders fit, so they're asked rather
 * than guessed.
 *
 * The three answers are exactly the `PrequalifyingBusinessDetails` row, which
 * this step writes on its way out — the funding step's row depends on it
 * existing.
 */
export function BusinessStep({ companyUuid, answers, onNext, onBack }: Props) {
  const { mutateAsync: saveBusinessDetails } =
    useCustomerPrequalifyingBusinessDetailsUpdate()

  const form = useForm<BusinessAnswers>({
    resolver: zodResolver(schema, { errorMap: onboardingBusinessErrorMap }),
    defaultValues: answers ?? EMPTY,
  })

  const submit = form.handleSubmit(async (values) => {
    try {
      await saveBusinessDetails({ companyUuid, data: values })
      onNext(values)
    } catch (error) {
      // Whatever the server blames on a field lands under that field; the rest
      // arrives as the root error the step renders at its foot.
      applyErrorsToForm(form.setError, error as ErrorType<ErrorResponseType>)
    }
  })

  return (
    <OnboardingFormStep
      form={form}
      onSubmit={submit}
      title="Tell us about your business"
      description="A few things public records won’t reliably tell us — all of them shape which lenders are a fit."
      label="Continue"
      // Raw values, straight out of the fields: going back is not a submission,
      // so the resolver never runs and a half-filled step is kept as it stands.
      onBack={() => onBack(form.getValues())}
    >
      <FormSelect<BusinessAnswers>
        name="industry"
        options={IndustryEnumOptions}
        label="Industry"
        placeholder="Select your industry"
      />

      <FormChipGroup<BusinessAnswers>
        name="businessModel"
        label="How do you sell?"
        options={BusinessModelEnumOptions}
        variant="tile"
      />

      <FormSelect<BusinessAnswers>
        name="estimatedRevenue"
        options={EstimatedRevenueEnumOptions}
        label="Estimated Revenue"
        placeholder="Monthly Average Revenue"
      />
    </OnboardingFormStep>
  )
}
